// ============================================================================
// ConfirmDialog - the styled replacement for window.confirm() (Round 3, #4).
//
// Most staff actions should not ask at all: they happen, and a toast offers
// Undo (lib/undoable.js). This dialog is for the actions that CANNOT be taken
// back with a client write: a text or email goes out, an edge function
// rescores a member, a server function deletes a row the user cannot
// re-insert, a token is revoked. window.confirm() ignored the brand and dark
// mode, could not say which button is the dangerous one, and on some
// browsers offered "prevent this page from creating more dialogs".
//
// Two ways to use it:
//
//   1. Imperative (what the conversions use; reads like window.confirm):
//        if (!(await confirmDialog({ title: "Revoke this device?",
//              body: "Any check-in in progress is cancelled.",
//              confirmLabel: "Revoke", destructive: true }))) return;
//      Needs <ConfirmDialogHost/> mounted once (ToastProvider does it).
//
//   2. Declarative:
//        <ConfirmDialog open={!!pending} title=... onConfirm=... onCancel=... />
//
// Behaviour: portalled to <body>; focus moves into the dialog (Cancel for a
// destructive action, so a stray Enter never deletes; Confirm otherwise),
// Tab is trapped inside, Escape and a backdrop click cancel, and focus goes
// back to whatever opened it. Phones get a bottom sheet with 44px buttons.
// ============================================================================
import { useEffect, useRef, useState, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { C, FS, R } from "../lib/tokens";
import useIsMobile from "../hooks/useIsMobile";
import { Btn } from "./ui";
import { IconAlertTriangle, IconCheck } from "./icons";

const FOCUSABLE = "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export function ConfirmDialog({
  open, title, body = null, confirmLabel = "Confirm", cancelLabel = "Cancel",
  destructive = false, busy = false, onConfirm, onCancel,
}) {
  const isMobile = useIsMobile();
  const panelRef = useRef(null);
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);
  const returnFocusRef = useRef(null);
  const titleId = useId();
  const bodyId = useId();

  // Focus in on open, back out on close. Captured before the first paint of
  // the dialog so we know what to return to.
  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = typeof document !== "undefined" ? document.activeElement : null;
    const t = setTimeout(() => {
      // Btn does not forward a ref, so reach the button through its wrapper.
      const target = destructive ? cancelRef.current : (confirmRef.current && confirmRef.current.querySelector("button"));
      if (target) target.focus();
    }, 0);
    return () => {
      clearTimeout(t);
      const back = returnFocusRef.current;
      if (back && typeof back.focus === "function" && document.contains(back)) back.focus();
    };
  }, [open, destructive]);

  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (!busy && onCancel) onCancel();
      return;
    }
    if (e.key !== "Tab" || !panelRef.current) return;
    const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [busy, onCancel]);

  if (!open || typeof document === "undefined") return null;

  // Buttons: on phones they stack full width (primary on top, the thumb's
  // first reach); on desktop Cancel sits left of the action.
  const btnStyle = isMobile ? { width: "100%", minHeight: 44 } : { minWidth: 96 };
  const confirmBtn = (
    <span ref={confirmRef} data-testid="confirm-dialog-confirm" style={{ display: isMobile ? "block" : "inline-flex" }}>
      <Btn variant={destructive ? "danger" : "primary"} onClick={onConfirm} disabled={busy} style={btnStyle}>
        {busy ? "Working..." : confirmLabel}
      </Btn>
    </span>
  );
  const cancelBtn = (
    <button
      ref={cancelRef}
      type="button"
      onClick={onCancel}
      disabled={busy}
      data-testid="confirm-dialog-cancel"
      style={{
        ...btnStyle,
        // Neutral, not brand: in dark the outline variant is a solid brand
        // fill and Cancel read exactly like the action beside it.
        background: "var(--pos-btn-ghost-bg)", color: "var(--pos-btn-ghost-fg)",
        border: "0.5px solid " + C.borderLight, boxShadow: "none",
        borderRadius: R.card, padding: "9px 18px", fontSize: FS.body, fontWeight: 600,
        fontFamily: "inherit", cursor: busy ? "not-allowed" : "pointer",
      }}
    >{cancelLabel}</button>
  );

  const node = (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy && onCancel) onCancel(); }}
      onKeyDown={onKeyDown}
      data-testid="confirm-dialog-backdrop"
      style={{
        position: "fixed", inset: 0, zIndex: 3500, background: C.scrim,
        backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)",
        display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center",
        padding: isMobile ? 0 : 16, animation: "posFadeIn 0.15s ease",
      }}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={body ? bodyId : undefined}
        data-testid="confirm-dialog"
        data-variant={destructive ? "destructive" : "default"}
        style={{
          width: "100%", maxWidth: isMobile ? "100%" : 420,
          background: C.bgPrimary, color: C.textPrimary,
          borderRadius: isMobile ? R.sheet + "px " + R.sheet + "px 0 0" : R.sheet,
          border: "0.5px solid " + C.borderLight,
          boxShadow: C.elevation.overlay,
          padding: isMobile ? "20px 16px calc(16px + env(safe-area-inset-bottom, 0px))" : "22px 22px 18px",
          animation: isMobile ? "posSheetIn 0.24s cubic-bezier(0.16,1,0.3,1)" : "posModalIn 0.2s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {/* The badge tells the eye which kind of question this is before
              the words do: red triangle for "this cannot be taken back",
              brand check for "this sends / starts something". */}
          <div aria-hidden="true" style={{
            width: 36, height: 36, borderRadius: R.pill, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: destructive ? C.dangerBg : "var(--brand-bg, " + C.infoBg + ")",
            color: destructive ? C.dangerFg : "var(--brand-primary, " + C.infoFg + ")",
          }}>
            {destructive ? <IconAlertTriangle size={18} /> : <IconCheck size={18} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id={titleId} style={{ fontSize: FS.title, fontWeight: 700, lineHeight: 1.3, color: C.textPrimary }}>{title}</div>
            {body && (
              <div id={bodyId} style={{ fontSize: FS.body, lineHeight: 1.5, color: C.textSecondary, marginTop: 6, whiteSpace: "pre-line" }}>{body}</div>
            )}
          </div>
        </div>
        <div style={{
          display: "flex", gap: 8, marginTop: 20,
          flexDirection: isMobile ? "column-reverse" : "row", justifyContent: "flex-end",
        }}>
          {cancelBtn}
          {confirmBtn}
        </div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}

// ---- Imperative host ---------------------------------------------------------
// One pending question at a time; a second call while one is open cancels the
// first (resolves false) so a caller can never be left awaiting forever.
let open = null;

export function confirmDialog(opts) {
  if (!open) {
    // No host mounted (a unit test, or a tree outside ToastProvider). Fail
    // closed: an unasked question is a "no", never a silent "yes".
    if (typeof console !== "undefined") console.warn("confirmDialog() called before ConfirmDialogHost mounted");
    return Promise.resolve(false);
  }
  return open(opts || {});
}

export function ConfirmDialogHost() {
  const [req, setReq] = useState(null);
  const resolver = useRef(null);

  const settle = useCallback((value) => {
    const r = resolver.current;
    resolver.current = null;
    setReq(null);
    if (r) r(value);
  }, []);

  useEffect(() => {
    open = (opts) => new Promise((resolve) => {
      if (resolver.current) resolver.current(false);
      resolver.current = resolve;
      setReq(opts);
    });
    return () => { open = null; if (resolver.current) resolver.current(false); };
  }, []);

  return (
    <ConfirmDialog
      open={!!req}
      title={req ? req.title : ""}
      body={req ? req.body : null}
      confirmLabel={req && req.confirmLabel ? req.confirmLabel : "Confirm"}
      cancelLabel={req && req.cancelLabel ? req.cancelLabel : "Cancel"}
      destructive={!!(req && req.destructive)}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
}

export default ConfirmDialog;
